// Analytics + advertising pixel loader (opt-out cookie model).
//
// Pixels load by default on first page view. Users can opt out via the
// cookie notice or the Cookie preferences link in the footer. GPC / DNT
// signals are honored as a hard decline so EU visitors arriving with GPC
// effectively get opt-in behavior automatically.
//
// API:
//   getConsent()           -> 'accepted' | 'declined' | 'unknown'
//                             (treated as accepted for loading purposes)
//   setConsent(state)      -> stores choice + (un)loads pixels + fires event
//   initAnalytics()        -> call once at app start; loads pixels unless
//                             explicitly declined or GPC/DNT is set
//   metaTrack(name, params)
//   gaTrack(name, params)
//   markStripeIntent / consumeStripeIntent  -> sessionStorage round-trip
//                             so Stripe-return URLs carry value to events
//
// To listen for consent changes (e.g. the cookie banner reopening):
//   window.addEventListener('sift:consent-changed', (e) => ... e.detail.state)

import { isNative } from "./platform";
import { blocksThirdPartyScripts } from "./tokenisedPaths";

export const GA_MEASUREMENT_ID = "G-VYXDVSXQFC";
export const TIKTOK_PIXEL_ID = "D8E8GJBC77UC2BMVOJVG";
export const GTM_CONTAINER_ID = "GTM-PMN9MCMW";
export const META_PIXEL_ID = "1305596617867361";
const STORAGE_KEY = "sift_cookie_consent";
const STRIPE_INTENT_KEY = "sift_stripe_intent";

let gaScriptLoaded = false;
let tiktokScriptLoaded = false;
let gtmScriptLoaded = false;
let metaPixelLoaded = false;

function readGpcSignal() {
  // navigator.globalPrivacyControl is the standard "Sec-GPC: 1" signal.
  // Treat DNT=1 the same way for older browsers.
  if (typeof navigator === "undefined") return false;
  if (navigator.globalPrivacyControl === true) return true;
  if (navigator.doNotTrack === "1" || navigator.doNotTrack === "yes") return true;
  return false;
}

export function getConsent() {
  if (typeof window === "undefined") return "unknown";
  if (readGpcSignal()) return "declined";
  try {
    const v = window.localStorage.getItem(STORAGE_KEY);
    if (v === "accepted" || v === "declined") return v;
  } catch {
    // localStorage might be blocked (private mode); treat as unknown so the
    // banner re-appears every session, which is the right safe default.
  }
  return "unknown";
}

// Signed in on this browser. Same keys lib/api.js reads the token from.
function isSignedIn() {
  try {
    return Boolean(
      window.localStorage.getItem("access") ||
      window.localStorage.getItem("bearer") ||
      window.localStorage.getItem("authToken") ||
      window.localStorage.getItem("token")
    );
  } catch {
    return false;
  }
}

// Third-party scripts never load inside the signed-in app (RT-CFG10). Every
// page that sends somebody to Stripe is signed in, and PCI DSS SAQ A asks that
// no script on those pages can change where the Pay button goes. GA, GTM and
// the two pixels can each run arbitrary code, and GTM runs whatever its
// container publishes. Conversions are still counted: the Stripe webhook
// sends Purchase and Subscribe to Meta server-side (accounts/meta_capi.py),
// and the first-party tracker (lib/tracker.js) keeps recording the signed-in
// app, since it only talks to our own API.
function thirdPartyScriptsBlocked() {
  if (isNative()) return true;
  if (typeof window === "undefined") return true;
  if (blocksThirdPartyScripts(window.location.pathname)) return true;
  return isSignedIn();
}

export function setConsent(state) {
  // Native apps never load tracking (see initAnalytics), so consent is a no-op
  // there. Guard defensively in case a stray caller invokes this on device.
  if (isNative()) return;
  if (state !== "accepted" && state !== "declined") return;
  try {
    window.localStorage.setItem(STORAGE_KEY, state);
  } catch {
    // ignore. See getConsent comment for context.
  }
  if (state === "accepted") {
    // The choice is stored and applies on the next signed-out page load.
    if (thirdPartyScriptsBlocked()) {
      window.dispatchEvent(new CustomEvent("sift:consent-changed", { detail: { state } }));
      return;
    }
    loadGoogleAnalytics();
    loadTikTokPixel();
    loadGoogleTagManager();
    loadMetaPixel();
  } else {
    disableGoogleAnalytics();
    disableTikTokPixel();
    disableMetaPixel();
    // GTM has no runtime disable hook; once loaded, container tags continue
    // until next page load. Declining before first load prevents injection.
  }
  window.dispatchEvent(new CustomEvent("sift:consent-changed", { detail: { state } }));
}

function loadGoogleAnalytics() {
  if (typeof window === "undefined") return;
  // Web only. Apple treats analytics cookies/identifiers loaded in the native
  // webview as tracking that would require App Tracking Transparency. The app
  // ships with no third-party tracking, so GA must never load on device
  // (matches the TikTok / GTM / Meta guards below).
  if (isNative()) return;
  // Clear any prior opt-out (in case the user accepted after declining).
  window[`ga-disable-${GA_MEASUREMENT_ID}`] = false;

  if (gaScriptLoaded) {
    return;
  }
  // Inject the gtag bootstrap inline (Google's snippet, ported from index.html).
  window.dataLayer = window.dataLayer || [];
  function gtag() { window.dataLayer.push(arguments); }
  window.gtag = gtag;
  gtag("js", new Date());
  gtag("config", GA_MEASUREMENT_ID, { anonymize_ip: true });

  const s = document.createElement("script");
  s.async = true;
  s.src = `https://www.googletagmanager.com/gtag/js?id=${GA_MEASUREMENT_ID}`;
  document.head.appendChild(s);
  gaScriptLoaded = true;
}

function disableGoogleAnalytics() {
  if (typeof window === "undefined") return;
  // Google's official opt-out flag. Even if the script is already loaded,
  // this stops further hits. We cannot remove the script that's in the DOM,
  // but no measurement protocol calls will fire after this is set.
  window[`ga-disable-${GA_MEASUREMENT_ID}`] = true;
}

function loadTikTokPixel() {
  if (typeof window === "undefined") return;
  // Web only. The mobile app ships no attribution SDK at all — see the
  // 5.1.2(i) fix; nothing here may run natively or it needs an ATT prompt.
  if (isNative()) return;
  if (tiktokScriptLoaded) {
    if (window.ttq && typeof window.ttq.grantConsent === "function") {
      window.ttq.grantConsent();
    }
    return;
  }
  // Ported from TikTok's official pixel snippet.
  const w = window;
  const d = document;
  const t = "ttq";
  w.TiktokAnalyticsObject = t;
  const ttq = (w[t] = w[t] || []);
  ttq.methods = ["page","track","identify","instances","debug","on","off","once","ready","alias","group","enableCookie","disableCookie","holdConsent","revokeConsent","grantConsent"];
  ttq.setAndDefer = function (target, method) {
    target[method] = function () {
      target.push([method].concat(Array.prototype.slice.call(arguments, 0)));
    };
  };
  for (let i = 0; i < ttq.methods.length; i++) ttq.setAndDefer(ttq, ttq.methods[i]);
  ttq.instance = function (id) {
    const e = ttq._i[id] || [];
    for (let n = 0; n < ttq.methods.length; n++) ttq.setAndDefer(e, ttq.methods[n]);
    return e;
  };
  ttq.load = function (id, opts) {
    const url = "https://analytics.tiktok.com/i18n/pixel/events.js";
    ttq._i = ttq._i || {};
    ttq._i[id] = [];
    ttq._i[id]._u = url;
    ttq._t = ttq._t || {};
    ttq._t[id] = +new Date();
    ttq._o = ttq._o || {};
    ttq._o[id] = opts || {};
    const n = d.createElement("script");
    n.type = "text/javascript";
    n.async = true;
    n.src = url + "?sdkid=" + id + "&lib=" + t;
    const first = d.getElementsByTagName("script")[0];
    first.parentNode.insertBefore(n, first);
  };
  ttq.load(TIKTOK_PIXEL_ID);
  ttq.page();
  tiktokScriptLoaded = true;
}

function disableTikTokPixel() {
  if (typeof window === "undefined") return;
  // If the pixel was already loaded earlier in the session, use TikTok's
  // own consent API to stop further hits. If it was never loaded, nothing
  // to do — declining simply means we never inject the script.
  if (window.ttq && typeof window.ttq.revokeConsent === "function") {
    window.ttq.revokeConsent();
  }
}

function loadGoogleTagManager() {
  if (typeof window === "undefined") return;
  // Web only. GTM running inside the Capacitor webview would be redundant
  // with the mobile attribution SDK and complicates app-store privacy
  // disclosures.
  if (isNative()) return;
  if (gtmScriptLoaded) return;
  // Ported from Google's official GTM snippet.
  window.dataLayer = window.dataLayer || [];
  window.dataLayer.push({ "gtm.start": new Date().getTime(), event: "gtm.js" });
  const s = document.createElement("script");
  s.async = true;
  s.src = `https://www.googletagmanager.com/gtm.js?id=${GTM_CONTAINER_ID}`;
  const first = document.getElementsByTagName("script")[0];
  first.parentNode.insertBefore(s, first);
  gtmScriptLoaded = true;
}

function loadMetaPixel() {
  if (typeof window === "undefined") return;
  // Web only. The mobile Capacitor app uses the AppsFlyer SDK for attribution
  // and would forward Meta install events through there, not the web pixel.
  if (isNative()) return;
  if (metaPixelLoaded) {
    // Pixel already loaded earlier this session; nothing extra to do.
    return;
  }
  // Ported from Meta's official base code (connect.facebook.net/en_US/fbevents.js).
  const f = window;
  const b = document;
  const e = "script";
  const v = "https://connect.facebook.net/en_US/fbevents.js";
  if (f.fbq) {
    f.fbq("init", META_PIXEL_ID);
    f.fbq("track", "PageView");
    metaPixelLoaded = true;
    return;
  }
  const n = (f.fbq = function () {
    n.callMethod
      ? n.callMethod.apply(n, arguments)
      : n.queue.push(arguments);
  });
  if (!f._fbq) f._fbq = n;
  n.push = n;
  n.loaded = true;
  n.version = "2.0";
  n.queue = [];
  const t = b.createElement(e);
  t.async = true;
  t.src = v;
  const s = b.getElementsByTagName(e)[0];
  s.parentNode.insertBefore(t, s);
  f.fbq("init", META_PIXEL_ID);
  f.fbq("track", "PageView");
  metaPixelLoaded = true;
}

function disableMetaPixel() {
  // Meta does not expose a runtime disable hook for fbq. If the pixel was
  // already injected this session, we route around it by no-op'ing metaTrack
  // when consent is not accepted (see metaTrack below). Declining before
  // first load prevents the script from ever loading.
}

// Fire a Meta Pixel event. No-ops if the user has opted out or if the pixel
// script hasn't loaded yet, so call sites don't need to guard.
//
//   metaTrack("CompleteRegistration");
//   metaTrack("Purchase", { currency: "USD", value: 1000 });
/* A custom Meta event, for things Meta has no standard name for.
 *
 * `fbq("track", ...)` is for Meta's own vocabulary (Purchase, Lead,
 * CompleteRegistration). Sending an invented name through it is accepted and
 * then quietly treated as unrecognised, which is the worst outcome: it looks
 * like it works and cannot be built into a custom conversion. Custom names
 * have to go through "trackCustom", and then they appear in Events Manager for
 * anyone building an audience out of them. */
export function metaTrackCustom(eventName, params) {
  if (typeof window === "undefined") return;
  if (getConsent() === "declined") return;
  if (typeof window.fbq !== "function") return;
  if (params && Object.keys(params).length > 0) {
    window.fbq("trackCustom", eventName, params);
  } else {
    window.fbq("trackCustom", eventName);
  }
}

export function metaTrack(eventName, params) {
  if (typeof window === "undefined") return;
  if (getConsent() === "declined") return;
  if (typeof window.fbq !== "function") return;
  if (params && Object.keys(params).length > 0) {
    window.fbq("track", eventName, params);
  } else {
    window.fbq("track", eventName);
  }
}

/* The company funnel, mirrored to Meta step by step.
 *
 * Every company-side clickstream event (trackEvent in lib/tracker.js calls
 * this) is also sent to Meta as a custom event under the SAME name, so
 * `need_draft_ready` in our data is `need_draft_ready` in Events Manager and
 * nobody has to translate. Company ads are running; Meta optimising on a
 * funnel it cannot see was the problem (2026-10-06: it saw ViewContent, then
 * nothing until CompleteRegistration).
 *
 * On top of that, a few steps also send the Meta STANDARD event that means the
 * same thing, because standard events are what delivery optimises on best:
 * entering the flow is ViewContent, asking for a post is Search. These replace
 * the hand-placed calls that used to sit on individual screens and went
 * missing when a screen retired (the job-board scan took ViewContent with it
 * on 2026-09-18).
 *
 * Company side means tagged account_type=organization, or a company_* or
 * hero_* event with no account_type at all (both only render on company
 * screens: the hero lives on BusinessLanding alone). Individuals are not sent.
 *
 * Only the tags below go to Meta, never what somebody typed. Each name fires
 * once a visit, so a step that re-renders cannot inflate a custom conversion;
 * the flag is only set once the pixel can take the event, so a visit that
 * starts before the script loads is not marked as sent. */
const META_STANDARD_FOR = {
  hero_funnel_opened: "ViewContent",
  company_doors_shown: "ViewContent",
  company_flow_shown: "ViewContent",
  hero_need_submitted: "Search",
  need_draft_requested: "Search",
};
// Already sent to Meta at their call sites, as CompleteRegistration + StartTrial,
// JobPosted and ActivatedHome. Mirroring them too would count each twice.
const META_SENT_ELSEWHERE = new Set([
  "account_created", "verification_completed", "first_posting_created", "activated_home",
]);
const META_SAFE_TAGS = ["flow", "arm", "door", "source", "intent", "via", "drafted_by"];

function isCompanySide(name, props) {
  const acct = props?.account_type;
  if (acct) return acct === "organization";
  return name.startsWith("company_") || name.startsWith("hero_");
}

function firstThisVisit(key) {
  try {
    const k = `sift_meta_once:${key}`;
    if (window.sessionStorage.getItem(k)) return false;
    window.sessionStorage.setItem(k, "1");
  } catch { /* storage blocked: send it, a duplicate beats a gap */ }
  return true;
}

export function mirrorToMeta(name, props) {
  if (typeof window === "undefined" || !name) return;
  if (getConsent() === "declined") return;
  if (typeof window.fbq !== "function") return;
  if (META_SENT_ELSEWHERE.has(name) || !isCompanySide(name, props)) return;

  const params = { ...signupAudience("organization") };
  for (const k of META_SAFE_TAGS) {
    const v = props?.[k];
    if (typeof v === "string" || typeof v === "number") params[k] = String(v).slice(0, 60);
  }
  if (firstThisVisit(`custom:${name}`)) metaTrackCustom(name, params);

  const standard = META_STANDARD_FOR[name];
  if (standard && firstThisVisit(`standard:${standard}`)) {
    metaTrack(standard, signupAudience("organization"));
  }
}

/* Which side of the marketplace a signup conversion came from.
 *
 * GA has carried `account_type` on `sign_up` for a long time. Meta never got
 * it: every call site fired a bare `metaTrack("CompleteRegistration")`, so in
 * Ads Manager a company signing up and a jobseeker signing up were the same
 * event with nothing to tell them apart. Anyone running company-targeted ads
 * could not build a custom conversion for the thing they were paying for, and
 * could not optimize delivery toward it either.
 *
 * Sent under two names on purpose. `content_category` is what Meta's custom
 * conversion builder offers as a filterable field without any setup;
 * `account_type` is the name every other surface here already uses, so the
 * dashboards and the pixel agree.
 *
 * Additive: the events themselves are unchanged, so an existing campaign
 * optimizing on CompleteRegistration keeps working exactly as before.
 */
export function signupAudience(accountType) {
  if (!accountType) return undefined;
  return { content_category: accountType, account_type: accountType };
}

// Fire a Google Analytics 4 event. No-ops if the user has opted out or if
// gtag hasn't loaded. Use GA4's standard event names where possible:
//   gaTrack("sign_up", { method: "email" })
//   gaTrack("begin_checkout", { value: 100, currency: "USD" })
//   gaTrack("purchase", { transaction_id: "tx_123", value: 100, currency: "USD" })
//   gaTrack("generate_lead", { value: 0, currency: "USD" })
// Standard list: https://developers.google.com/analytics/devguides/collection/ga4/reference/events
export function gaTrack(eventName, params) {
  if (typeof window === "undefined") return;
  if (getConsent() === "declined") return;
  if (typeof window.gtag !== "function") return;
  window.gtag("event", eventName, params || {});
}

// ---------------------------------------------------------------------------
// Ad-campaign attribution capture (fbclid, utm_*)
// ---------------------------------------------------------------------------
// Marketing campaigns drop visitors on the site with attribution params in
// the URL. We capture them once on landing and persist long enough to attach
// to backend conversion events (CompleteRegistration, Purchase) via the
// Meta Conversions API. Without this, CAPI matches only on hashed email and
// loses the campaign-level click ID that Meta uses for attribution.
//
// Meta's docs: https://developers.facebook.com/docs/marketing-api/conversions-api/parameters/fbp-and-fbc

const ATTRIBUTION_STORAGE_KEY = "sift_attribution";
const ATTRIBUTION_KEYS = ["fbclid", "utm_source", "utm_medium", "utm_campaign", "utm_content", "utm_term", "gclid", "ttclid", "msclkid"];
// Meta says fbc is valid for 90 days from click; we treat the local cache
// the same way and let a fresher landing param overwrite an older value.
const ATTRIBUTION_TTL_MS = 90 * 24 * 60 * 60 * 1000;

// Build a Meta-format fbc value: "fb.<subdomain_index>.<unix_ms>.<fbclid>"
// subdomain_index=1 for a single-subdomain site like siftcareers.com.
function buildFbc(fbclid, capturedAtMs) {
  if (!fbclid) return null;
  return `fb.1.${capturedAtMs || Date.now()}.${fbclid}`;
}

// Read attribution params off the current URL and merge into localStorage.
// Idempotent. Re-running on every navigation is safe — params that aren't
// in the URL don't overwrite cached ones.
export function captureAttribution() {
  if (typeof window === "undefined") return;
  try {
    const params = new URLSearchParams(window.location.search);
    const incoming = {};
    let any = false;
    for (const key of ATTRIBUTION_KEYS) {
      const v = params.get(key);
      if (v) {
        incoming[key] = v;
        any = true;
      }
    }
    if (!any) return;
    const prior = _readAttribution() || {};
    const merged = { ...prior, ...incoming, captured_at: Date.now() };
    if (incoming.fbclid) {
      merged.fbc = buildFbc(incoming.fbclid, merged.captured_at);
    }
    // Remember which page this ad/campaign dropped the visitor on (last-touch,
    // like the utm params above). Lets "Start free" route ad traffic to the right
    // audience even after the visitor has navigated away from the landing page.
    merged.landing_path = window.location.pathname;
    window.localStorage.setItem(ATTRIBUTION_STORAGE_KEY, JSON.stringify(merged));
  } catch {
    // localStorage blocked or URL parse failed; attribution gracefully
    // degrades to email-only CAPI matching.
  }
}

function _readAttribution() {
  try {
    const raw = window.localStorage.getItem(ATTRIBUTION_STORAGE_KEY);
    if (!raw) return null;
    const parsed = JSON.parse(raw);
    if (Date.now() - (parsed.captured_at || 0) > ATTRIBUTION_TTL_MS) {
      window.localStorage.removeItem(ATTRIBUTION_STORAGE_KEY);
      return null;
    }
    return parsed;
  } catch {
    return null;
  }
}

// Return a payload suitable for inclusion in a POST body to the Django
// backend. Always returns an object (possibly empty) so call sites can
// spread it unconditionally: `body: JSON.stringify({...form, ...getAttributionPayload()})`.
export function getAttributionPayload() {
  const a = _readAttribution();
  if (!a) return {};
  // Capture the landing URL (origin + path, no query) once. Useful for
  // GA4 + ad networks that want to know which page started the funnel.
  const out = {
    attribution_fbclid: a.fbclid || "",
    attribution_fbc: a.fbc || "",
    attribution_utm_source: a.utm_source || "",
    attribution_utm_medium: a.utm_medium || "",
    attribution_utm_campaign: a.utm_campaign || "",
    attribution_utm_content: a.utm_content || "",
    attribution_utm_term: a.utm_term || "",
    attribution_gclid: a.gclid || "",
    attribution_ttclid: a.ttclid || "",
    attribution_msclkid: a.msclkid || "",
    attribution_landing_path: a.landing_path || "",
    attribution_captured_at: a.captured_at ? new Date(a.captured_at).toISOString() : "",
  };
  return out;
}

// Stash the user's intended Stripe transaction so we can attach a value to
// the Subscribe / Purchase event after Stripe redirects back. Set this at
// click time (when we still know the price), read on the return URL.
//
// type: "subscribe" | "purchase"
// value: numeric USD amount (not cents)
export function markStripeIntent({ type, value, currency = "USD" }) {
  if (typeof window === "undefined") return;
  try {
    window.sessionStorage.setItem(
      STRIPE_INTENT_KEY,
      JSON.stringify({ type, value, currency, ts: Date.now() })
    );
  } catch {
    // sessionStorage blocked; events still fire without a value attached.
  }
}

export function consumeStripeIntent() {
  if (typeof window === "undefined") return null;
  try {
    const raw = window.sessionStorage.getItem(STRIPE_INTENT_KEY);
    if (!raw) return null;
    window.sessionStorage.removeItem(STRIPE_INTENT_KEY);
    const parsed = JSON.parse(raw);
    // Stale intents (>30 min old) are probably a different flow; ignore.
    if (Date.now() - (parsed.ts || 0) > 30 * 60 * 1000) return null;
    return parsed;
  } catch {
    return null;
  }
}

export function initAnalytics() {
  // Native apps do not track. We load NO analytics or advertising scripts in
  // the Capacitor webview (no GA, GTM, TikTok, or Meta pixel) and set no
  // tracking cookies, so the app needs no App Tracking Transparency prompt.
  // This is the single chokepoint that keeps the iOS/Android apps tracker-free.
  if (isNative()) return;

  // Load nothing on a page whose URL is a credential.
  //
  // GA4, GTM, the Meta pixel and the TikTok pixel each read window.location
  // themselves on load, so configuring them one by one (send_page_view: false,
  // a scrubbed page_location, and so on) leaves whichever one is added next
  // leaking again. Not loading them is the version that stays fixed.
  //
  // It also costs nothing. A signing or payment page has no marketing value:
  // the visitor arrived from a link in an email, there is no acquisition to
  // attribute, and the page is a transaction rather than a funnel step.
  //
  // Nor inside the signed-in app; see thirdPartyScriptsBlocked.
  if (thirdPartyScriptsBlocked()) return;

  const state = getConsent();
  // Opt-out model: load pixels unless the user has explicitly declined or
  // their browser sent a GPC/DNT signal. 'unknown' (first visit, no choice
  // yet) is treated as implicit accept, with the cookie notice offering an
  // opt-out path. getConsent() already returns 'declined' when GPC is set.
  if (state === "declined") {
    disableGoogleAnalytics();
    disableTikTokPixel();
    disableMetaPixel();
    return;
  }
  loadGoogleAnalytics();
  loadTikTokPixel();
  // GTM re-enabled. IMPORTANT: the container (GTM-PMN9MCMW) must NOT contain a
  // Meta Pixel tag — loadMetaPixel() below already fires the pixel directly, so
  // a pixel tag in GTM would double-fire PageView (the original bug). Keep GTM
  // for non-Meta tags only, or remove loadMetaPixel and let GTM own the pixel.
  loadGoogleTagManager();
  loadMetaPixel();
}
